// SPDX-License-Identifier: MIT
import { t } from '../i18n/t';
import type { Provider } from './picker';
import { detectPlatform } from '../platform';

const PROVIDER_NAMES: Record<Provider, string> = {
  anthropic: 'Anthropic',
  gemini: 'Gemini',
  openai: 'OpenAI',
};

export interface AuthModalOptions {
  providerPref: Provider;
  onClose?: () => void;
  onGoogleClick?: () => void | Promise<void>;
  onMagicLinkSubmit?: (email: string) => void | Promise<void>;
  // v1.1.0 — OTP code fallback for email-prefetch breakage. After the
  // magic-link is sent, the SENT state offers BOTH the click-the-link
  // path AND a 6-digit code input (the email contains both). Caller
  // resolves promise on success → modal closes + onCodeVerifySuccess
  // navigates to /welcome. Throws on invalid/expired code → modal shows
  // a code-specific error.
  onOtpCodeSubmit?: (email: string, code: string) => void | Promise<void>;
  onCodeVerifySuccess?: () => void;
}

export function renderAuthModal(opts: AuthModalOptions): HTMLElement {
  const el = document.createElement('div');
  el.className = 'auth-modal';
  el.setAttribute('role', 'dialog');
  el.setAttribute('aria-modal', 'true');
  el.innerHTML = buildInitialMarkup(opts.providerPref);

  const close = () => {
    if (el.parentElement) el.parentElement.removeChild(el);
    opts.onClose?.();
  };

  // Backdrop click (self-only) closes; card click does NOT bubble.
  el.addEventListener('click', (e) => {
    if (e.target === el) close();
  });
  el.querySelector<HTMLElement>('.auth-modal__card')!.addEventListener('click', (e) => {
    e.stopPropagation();
  });

  // Close button
  el.querySelector<HTMLElement>('[data-component="auth-close"]')!.addEventListener('click', close);

  // Google OAuth button
  el.querySelector<HTMLElement>('[data-component="auth-google"]')!.addEventListener('click', async () => {
    try {
      await opts.onGoogleClick?.();
    } catch {
      showError(el);
    }
  });

  // Magic-link submit — wrapped in busy-state + de-dup guard so the
  // user can't spam emails by clicking 5x during a slow SMTP path.
  // 2026-05-16 incident root cause : GoTrue+Resend occasionally took
  // 10s+ to respond ; the button gave no feedback so the user clicked
  // repeatedly, each click firing another signInWithOtp → multiple
  // magic-link emails of which only the latest worked. The aria-busy
  // attribute + early return on busy plug both holes.
  const submitBtn = el.querySelector<HTMLButtonElement>('[data-component="auth-magic-link-submit"]')!;
  const initialSubmitLabel = submitBtn.textContent ?? '';
  const submitHandler = wrapSubmitWithBusyDedup(submitBtn, initialSubmitLabel, async () => {
    const input = el.querySelector<HTMLInputElement>('[data-component="auth-email-input"]')!;
    const email = input.value.trim();
    if (!email) return;
    clearError(el);
    try {
      await opts.onMagicLinkSubmit?.(email);
      el.classList.add('auth-modal--sent');
      el.querySelector<HTMLElement>('.auth-modal__card')!.innerHTML = buildSentMarkup(email);
      wireSentState(el, email, opts, close);
    } catch {
      showError(el);
    }
  });
  submitBtn.addEventListener('click', submitHandler);
  el.querySelector<HTMLElement>('[data-component="auth-email-input"]')!.addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      void submitHandler();
    }
  });

  return el;
}

// Wraps an async submit handler with a busy/dedup guard. While the inner
// handler is in flight, the button is aria-busy + disabled + label-swapped,
// and subsequent calls (click OR Enter) short-circuit instead of re-firing
// the inner handler. Even if the user clicks 10x, only the FIRST call
// reaches the network. Used by both the email submit + (future) other
// async submits in the modal.
function wrapSubmitWithBusyDedup(
  btn: HTMLButtonElement,
  idleLabel: string,
  inner: () => Promise<void>,
): () => Promise<void> {
  let busy = false;
  return async () => {
    if (busy) return;
    busy = true;
    btn.setAttribute('aria-busy', 'true');
    btn.disabled = true;
    btn.textContent = t('authModal.emailSubmitBusy');
    try {
      await inner();
    } finally {
      busy = false;
      btn.removeAttribute('aria-busy');
      btn.disabled = false;
      // Only reset label if we're still in the same DOM ; if the inner
      // handler swapped the card markup (e.g. SENT state), the button
      // no longer exists and the label reset is a harmless no-op.
      if (btn.isConnected) btn.textContent = idleLabel;
    }
  };
}

// v1.1.0 — Wires the SENT state's interactive elements : close button,
// back-link (returns to initial state), and the new 6-digit OTP code input
// + submit. The OTP code path is the prefetch-immune fallback when the
// magic-link URL gets consumed by webmail/scanner before the user clicks.
function wireSentState(el: HTMLElement, email: string, opts: AuthModalOptions, close: () => void): void {
  el.querySelector<HTMLElement>('[data-component="auth-close"]')!.addEventListener('click', close);
  el.querySelector<HTMLElement>('[data-component="auth-back"]')!.addEventListener('click', () => {
    el.classList.remove('auth-modal--sent');
    el.querySelector<HTMLElement>('.auth-modal__card')!.innerHTML = buildInitialMarkup(opts.providerPref);
    wireInitialState(el, opts, close);
  });

  const codeInput = el.querySelector<HTMLInputElement>('[data-component="auth-code-input"]');
  const codeSubmit = el.querySelector<HTMLElement>('[data-component="auth-code-submit"]');
  if (!codeInput || !codeSubmit) return;

  // Restrict to digits only (paste-safe). Code is exactly 6 digits per Supabase OTP spec.
  codeInput.addEventListener('input', () => {
    const digits = codeInput.value.replace(/\D/g, '').slice(0, 6);
    if (codeInput.value !== digits) codeInput.value = digits;
  });

  const verifyHandler = async () => {
    const code = codeInput.value.trim();
    if (code.length !== 6) {
      showCodeError(el);
      return;
    }
    clearError(el);
    codeSubmit.setAttribute('aria-busy', 'true');
    try {
      await opts.onOtpCodeSubmit?.(email, code);
      // Success — close modal, caller navigates to /welcome.
      close();
      opts.onCodeVerifySuccess?.();
    } catch {
      showCodeError(el);
    } finally {
      codeSubmit.removeAttribute('aria-busy');
    }
  };
  codeSubmit.addEventListener('click', verifyHandler);
  codeInput.addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      void verifyHandler();
    }
  });
}

function wireInitialState(el: HTMLElement, opts: AuthModalOptions, close: () => void): void {
  el.querySelector<HTMLElement>('[data-component="auth-close"]')!.addEventListener('click', close);
  el.querySelector<HTMLElement>('[data-component="auth-google"]')!.addEventListener('click', async () => {
    try { await opts.onGoogleClick?.(); } catch { showError(el); }
  });
  // Mirror the busy-dedup wrapper from renderAuthModal so the SENT→back
  // path benefits from the same anti-spam guard.
  const submitBtn = el.querySelector<HTMLButtonElement>('[data-component="auth-magic-link-submit"]')!;
  const initialSubmitLabel = submitBtn.textContent ?? '';
  const submitHandler = wrapSubmitWithBusyDedup(submitBtn, initialSubmitLabel, async () => {
    const input = el.querySelector<HTMLInputElement>('[data-component="auth-email-input"]')!;
    const email = input.value.trim();
    if (!email) return;
    clearError(el);
    try {
      await opts.onMagicLinkSubmit?.(email);
      el.classList.add('auth-modal--sent');
      el.querySelector<HTMLElement>('.auth-modal__card')!.innerHTML = buildSentMarkup(email);
      wireSentState(el, email, opts, close);
    } catch { showError(el); }
  });
  submitBtn.addEventListener('click', submitHandler);
  el.querySelector<HTMLElement>('[data-component="auth-email-input"]')!.addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      void submitHandler();
    }
  });
  el.querySelector<HTMLElement>('.auth-modal__card')!.addEventListener('click', (e) => e.stopPropagation());
}

function showError(el: HTMLElement): void {
  let err = el.querySelector<HTMLElement>('[data-component="auth-error"]');
  if (!err) {
    err = document.createElement('div');
    err.setAttribute('data-component', 'auth-error');
    err.className = 'auth-modal__error';
    const card = el.querySelector<HTMLElement>('.auth-modal__card')!;
    card.appendChild(err);
  }
  err.textContent = t('authModal.errorGeneric');
}

// v1.1.0 — code-specific error (distinct copy from generic auth error).
function showCodeError(el: HTMLElement): void {
  let err = el.querySelector<HTMLElement>('[data-component="auth-error"]');
  if (!err) {
    err = document.createElement('div');
    err.setAttribute('data-component', 'auth-error');
    err.className = 'auth-modal__error';
    const card = el.querySelector<HTMLElement>('.auth-modal__card')!;
    card.appendChild(err);
  }
  err.textContent = t('authModal.codeErrorInvalid');
}

function clearError(el: HTMLElement): void {
  el.querySelector('[data-component="auth-error"]')?.remove();
}

function buildInitialMarkup(providerPref: Provider): string {
  const providerName = PROVIDER_NAMES[providerPref];
  return `
    <div class="auth-modal__card">
      <button data-component="auth-close" class="auth-modal__close" aria-label="${t('authModal.closeLabel')}" type="button">×</button>
      <div class="auth-modal__header">
        <h2 data-component="auth-modal-title" class="auth-modal__title">${t('authModal.title')}</h2>
        <div data-component="auth-modal-subtitle" class="auth-modal__subtitle">${t('authModal.subtitlePrefix')}<span class="auth-modal__provider">${providerName}</span>${t('authModal.subtitleSuffix')}</div>
      </div>
      <button data-component="auth-google" class="auth-modal__google" type="button">
        <span class="auth-modal__google-glyph" aria-hidden="true"></span>
        ${t('authModal.googleButton')}
      </button>
      <div class="auth-modal__divider">
        <span class="auth-modal__divider-line"></span>
        <span class="auth-modal__divider-text">${t('authModal.dividerOr')}</span>
        <span class="auth-modal__divider-line"></span>
      </div>
      <label class="auth-modal__label">${t('authModal.emailLabel')}</label>
      <input data-component="auth-email-input" class="auth-modal__input" type="email" placeholder="${t('authModal.emailPlaceholder')}" autocomplete="email">
      <button data-component="auth-magic-link-submit" class="auth-modal__submit" type="button">${t('authModal.emailSubmit')}</button>
      <div class="auth-modal__fine-print">
        ${t('authModal.finePrintBefore')}<a class="auth-modal__link" href="#cgu">${t('authModal.finePrintCgu')}</a>${t('authModal.finePrintAnd')}<a class="auth-modal__link" href="#privacy">${t('authModal.finePrintPrivacy')}</a>${t('authModal.finePrintAfter')}<br>
        ${t('authModal.finePrintNoPwd')}
      </div>
    </div>
  `;
}

function buildSentMarkup(email: string, isDesktop = detectPlatform() === 'desktop'): string {
  // On DESKTOP the magic LINK is a trap: clicking it opens the system browser and
  // signs the user into the web app, NOT this Tauri window — so its session never
  // lands here. The 6-digit CODE (verifyOtp, no redirect) is the only path that
  // signs you into the app. So desktop leads with the code and replaces the
  // "click the link" steps with a clear hint; web keeps the link-first flow.
  const guidance = isDesktop
    ? `<div class="auth-modal__sent-steps"><div class="auth-modal__desktop-hint">${t('authModal.sentDesktopHint')}</div></div>`
    : `
    <div class="auth-modal__sent-steps">
      <div class="auth-modal__sent-steps-title">${t('authModal.sentStepsTitle')}</div>
      <ol class="auth-modal__sent-steps-list">
        <li>${t('authModal.sentStep1')}</li>
        <li>${t('authModal.sentStep2')}</li>
        <li>${t('authModal.sentStep3')}</li>
      </ol>
    </div>
    <div class="auth-modal__divider auth-modal__divider--sent">
      <span class="auth-modal__divider-line"></span>
      <span class="auth-modal__divider-text">${t('authModal.dividerOr')}</span>
      <span class="auth-modal__divider-line"></span>
    </div>`;
  return `
    <button data-component="auth-close" class="auth-modal__close" aria-label="${t('authModal.closeLabel')}" type="button">×</button>
    <div class="auth-modal__sent-icon" aria-hidden="true">${isDesktop ? '🔢' : '📧'}</div>
    <h2 class="auth-modal__title auth-modal__title--sent">${isDesktop ? t('authModal.sentDesktopTitle') : t('authModal.sentTitle')}</h2>
    <div class="auth-modal__sent-body">
      ${t('authModal.sentBodyPrefix')}<br>
      <span data-component="auth-sent-email" class="auth-modal__sent-email">${escapeHtml(email)}</span>
    </div>
    ${guidance}
    <label class="auth-modal__label" for="auth-code-input">${t('authModal.sentCodeLabel')}</label>
    <div class="auth-modal__code-row">
      <input data-component="auth-code-input" id="auth-code-input" class="auth-modal__input auth-modal__input--code" type="text" inputmode="numeric" maxlength="6" autocomplete="one-time-code" placeholder="${t('authModal.sentCodePlaceholder')}">
      <button data-component="auth-code-submit" class="auth-modal__submit auth-modal__submit--code" type="button">${t('authModal.sentCodeSubmit')}</button>
    </div>
    <div class="auth-modal__code-helper">${t('authModal.sentCodeHelper')}</div>
    <div class="auth-modal__resend-prompt">${t('authModal.sentResendPrompt')}</div>
    <a class="auth-modal__resend-cta" href="#">${t('authModal.sentResendCta')}</a>
    <div class="auth-modal__back-wrap">
      <a data-component="auth-back" class="auth-modal__back" href="#">${t('authModal.sentBackLink')}</a>
    </div>
  `;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
